import { safeParse } from '../utils/helpers';
import { apiCall, makeApiAction } from './actions';
import {
  balanceHistoryByRestId,
  bookingSettlementsByRestId,
  settlementsByRestId,
  netBalanceByRestId,
  lookupPaymentMethodName,
  paymentMethods,
  getVGSAccessToken,
} from '../graphql/queries';
import {
  createCCSetupIntent,
  createDDSetupIntent,
  createVGSPaymentMethod,
  payBalance,
  savePaymentDetails,
} from '../graphql/mutations';
import { createAsyncAction } from '../lib/react-redux';

export const fetchBookingSettlementsByRestIdAction =
  (limit = 1000, offset = 0) =>
  (dispatch) => {
    dispatch(
      makeApiAction(
        'FETCH_BOOKING_SETTLEMENTS',
        bookingSettlementsByRestId,
        { limit, offset },
        {},
        'bookingSettlementsByRestId',
        true,
        dispatch,
      ),
    );
  };

export const fetchSettlementsByRestIdAction =
  (pageSize = 500, page = 0) =>
  (dispatch) => {
    dispatch(
      makeApiAction(
        'FETCH_SETTLEMENTS',
        settlementsByRestId,
        { pageSize, page },
        {},
        'settlementsByRestId',
        true,
        dispatch,
      ),
    );
  };

export const fetchBalanceHistoryByRestIdAction = () => (dispatch) => {
  dispatch(
    makeApiAction(
      'FETCH_BILLING_HISTORY',
      balanceHistoryByRestId,
      {},
      {},
      'balanceHistoryByRestId',
      true,
      dispatch,
    ),
  );
};

export const fetchNetBalanceByRestIdAction = () => (dispatch) => {
  dispatch(
    makeApiAction(
      'FETCH_NET_BALANCE',
      netBalanceByRestId,
      {},
      {},
      'netBalanceByRestId',
      true,
      dispatch,
    ),
  );
};

export const fetchPaymentMethodsAction = () => (dispatch) => {
  dispatch(
    makeApiAction(
      'FETCH_PAYMENT_METHODS',
      paymentMethods,
      {},
      {},
      'paymentMethods',
      true,
      dispatch,
    ),
  );
};

export const payBalanceAction =
  (amount, paymentMethodId, paymentMethodName, paymentMethodType) => (dispatch) => {
    return dispatch(
      makeApiAction(
        'PAY_BALANCE',
        payBalance,
        { amount, paymentMethodId, paymentMethodName, paymentMethodType },
        {},
        'payBalance',
        true,
        dispatch,
        'Payment made.',
        null,
        false,
        null,
        false,
        null,
        (response) => {
          const data = safeParse(response?.data?.payBalance);
          if (data?.status === 'failure') {
            return data?.paymentIntentStatus;
          }

          return true;
        },
      ),
    );
  };

export const createCCSetupIntentAction = () => {
  return apiCall(createCCSetupIntent, {}, true);
};

export const createDDSetupIntentAction = () => {
  return apiCall(createDDSetupIntent, {}, true);
};

export const getVGSAccessTokenAction = () => {
  return apiCall(getVGSAccessToken, {}, false);
};

/**
 * Registers a VGS-tokenized card (NZ) and resolves it into
 * { paymentMethodId, paymentMethodName, paymentMethodType } for savePaymentDetailsAction.
 *
 * createVGSPaymentMethod returns AWSJSON, like createCCSetupIntent/savePaymentDetails -
 * parsed here rather than left to the caller. paymentMethodName/paymentMethodType aren't
 * confirmed to come back from BE yet, so we fall back to building a display name from the
 * card brand/last4 (matching how Stripe/Airwallex cards are displayed) and default the type
 * to 'card'.
 */
export const createVGSPaymentMethodAction = async (vgsResponse) => {
  const { response, error, status } = await apiCall(
    createVGSPaymentMethod,
    {
      cardId: vgsResponse.data.id,
      vaultId: vgsResponse.metadata.observability.vault_id,
      expMonth: parseInt(vgsResponse.data.attributes.exp_month, 10),
      expYear: parseInt(vgsResponse.data.attributes.exp_year, 10),
      tokenType: vgsResponse.data.attributes.token_type,
      panAlias: vgsResponse.data.attributes.pan_alias,
      cvcAlias: vgsResponse.data.attributes.cvc_alias ?? '',
      cardFingerprint: vgsResponse.data.attributes.card_fingerprint,
      last4: vgsResponse.data.attributes.last4,
      cardBrand: vgsResponse.data.attributes.card_brand,
      cardType: vgsResponse.data.attributes.card_type,
      vgsTraceId: vgsResponse.metadata.observability.trace_id,
    },
    false,
  );

  if (error) {
    return { response, error, status };
  }

  // createVGSPaymentMethod returns { objectId, cardId, last4, cardBrand, cardType, ... } -
  // objectId is the id to carry forward into savePaymentDetails, matching the
  // createOnboardingPaymentMethod/completeSignupVGS pattern in ec-venue-signup.
  const parsed = safeParse(response?.data?.createVGSPaymentMethod) ?? {};
  const { cardBrand, last4 } = vgsResponse.data.attributes;

  return {
    response,
    error,
    status,
    paymentMethodId: parsed.objectId,
    paymentMethodName: `${cardBrand} ending in ${last4}`,
    paymentMethodType: 'card',
  };
};

/**
 * Save the payment details for airwallex or stripe.
 * For airwallex, an empty decline code means it was successful
 */
export const savePaymentDetailsAction = (
  paymentMethodId = null,
  paymentMethodName = null,
  paymentMethodType = null,
  declineCode = null,
) =>
  createAsyncAction(
    'SAVE_PAYMENT_DETAILS',
    savePaymentDetails,
    { paymentMethodId, paymentMethodName, paymentMethodType, declineCode },
    'savePaymentDetails',
    !declineCode ? 'Payment details saved.' : undefined,
    true,
    true,
  );

export const lookupPaymentMethodNameAction = (paymentMethodId) => {
  return apiCall(lookupPaymentMethodName, { paymentMethodId }, true);
};
